import React from "react";
import { Link } from "@reach/router";
import Helmet from "react-helmet";

import Navigation from "../components/WaveNavigation";
import AppInfo from "../components/AppInfo";
import ImagePlaceholder from "../components/ImagePlaceholder";
import Quiz from "../components/Quiz";
import { DESTINATIONS, TOWN_ORDER } from "../data/exploreCommunities";
import surpriseHero from "../assets/img/explore/lighthouse.jpg";
import lighthouseImg from "../assets/img/explore/lighthouse.jpg";
import sunkenForestImg from "../assets/img/explore/sunken-forest.jpg";
import waterTaxiImg from "../assets/img/explore/water-taxi-2.png";
import "./explore.scss";

const WATER_TAXI_DAYS = [
  "Ocean Beach → Lighthouse (lunch, shops + sightseeing)",
  "Kismet → Ocean Bay Park → Sunken Forest (full island traverse)",
];

const PLAN_LINKS = [
  {
    icon: "far fa-car",
    title: "Parking, by terminal",
    desc: "Quick summary of parking and Uber/Lyft drop-off locations by terminal.",
    to: "/info/parking",
    cta: "Full parking guide",
  },
  {
    icon: "far fa-train",
    title: "Coming from the city?",
    desc: "LIRR riders: David's Buses runs a shuttle from the train to the ferry terminals.",
    to: "/info/planning-your-trip",
    cta: "Shuttle info",
  },
  {
    icon: "far fa-question-circle",
    title: "Got questions?",
    desc: "We've got answers. Everything you need to know before you go.",
    to: "/info/faq",
    cta: "Visit the FAQ",
  },
];

export default () => (
  <div>
    <Helmet
      title="Fire Island Ferries | Explore"
      meta={[
        {
          name: "description",
          content:
            "New to Fire Island? Take our quick quiz to find your perfect beach community, explore the towns we service, and plan your trip across the Great South Bay.",
        },
        {
          name: "keywords",
          content:
            "Fire Island, explore, beach communities, quiz, Kismet, Ocean Beach, Fair Harbor, water taxi",
        },
      ]}
    />
    {/* SECTION 1 — HERO (full-bleed lighthouse image behind the wave nav) */}
    <section className="explore-hero" id="top">
      <div className="explore-hero-media" />
      <Navigation />
      <div className="container explore-hero-content">
        <h2>Explore</h2>
        <h1>Fire Island like a local.</h1>
        <p>
          Never been to the barrier island? You're in the right place. Here's
          everything you need to find your spot, plan your day, and make the
          most of the crossing.
        </p>
        <div className="explore-hero-ctas">
          <a className="btn btn-light" href="#quiz">
            Find my perfect spot →
          </a>
          <a className="btn btn-outline-light" href="#communities">
            Explore the communities →
          </a>
        </div>
      </div>
    </section>

    {/* SECTION 2 — QUIZ */}
    <section className="explore-section explore-quiz" id="quiz">
      <div className="container">
        <div className="explore-heading text-center">
          <h2>Your perfect Fire Island day is just a few clicks away.</h2>
          <p>
            Not sure which community is right for you? Answer a few quick
            questions and we'll match you with a destination — complete with a
            sample day and everything you need to plan the trip.
          </p>
        </div>
        <Quiz />
      </div>
    </section>

    {/* SECTION 3 — THE COMMUNITIES */}
    <section className="explore-section explore-communities" id="communities">
      <div className="container">
        <div className="explore-heading">
          <h2>The communities.</h2>
          <p>
            Fire Island is home to 22 unique communities, 9 of which we service
            via ferries. Every community has its own personality, its own
            regular crowd, and its own particular magic. Here's a quick look at
            each one — click any card to go deeper.
          </p>
        </div>
        <div className="row">
          {TOWN_ORDER.map((name) => {
            const d = DESTINATIONS[name];
            return (
              <div className="col-sm-6 col-lg-4" key={name}>
                <Link className="community-card" to={`/communities/${d.slug}`}>
                  <div className="cc-img">
                    <ImagePlaceholder
                      label={d.hero}
                      rounded={false}
                      src={d.heroImage}
                    />
                  </div>
                  <div className={`cc-body ${d.slug}-border`}>
                    <h3>{name}</h3>
                    <p className="cc-tagline">{d.gridTagline}</p>
                    <p className="cc-amenities">{d.amenitiesLine}</p>
                    <p className="cc-ferry">
                      <strong>Ferry from:</strong> {d.ferryFrom}
                    </p>
                    <span className="cc-link">Explore {name} →</span>
                  </div>
                </Link>
              </div>
            );
          })}

          {/* Surprise Me card */}
          <div className="col-sm-6 col-lg-4">
            <a className="community-card surprise-card" href="#quiz">
              <div className="cc-img">
                <ImagePlaceholder
                  label="A question mark over a blurry Fire Island scene"
                  rounded={false}
                  src={surpriseHero}
                  objectPosition="center 10%"
                />
              </div>
              <div className="cc-body">
                <h3>✦ Surprise Me — A Fire Island Day</h3>
                <p className="cc-tagline">
                  From the Lighthouse to fishing to shops and quiet beaches.
                </p>
                <span className="cc-link">
                  Take the quiz to unlock this itinerary →
                </span>
              </div>
            </a>
          </div>
        </div>

        {/* Beyond the towns callout */}
        <div className="beyond-callout">
          <div className="beyond-text">
            <h3>Beyond the towns</h3>
            <p>
              Two of Fire Island's most beloved spots aren't communities at all
              — climb the Fire Island Lighthouse for panoramic bay views, or
              wander the globally rare maritime holly forest at Sailors Haven.
              Both are reachable by{" "}
              <a
                href="https://www.fireislandwatertaxi.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fire Island Water Taxi
              </a>
              .
            </p>
          </div>
          <div className="beyond-cards">
            <Link className="beyond-card" to="/communities/fi-lighthouse">
              <div className="bc-img">
                <ImagePlaceholder
                  label="Fire Island Lighthouse from the dunes, late afternoon light"
                  rounded={false}
                  src={lighthouseImg}
                  objectPosition="center 30%"
                />
              </div>
              <div className="bc-body">
                <span className="bc-name">Fire Island Lighthouse</span>
                <span className="bc-arrow">→</span>
              </div>
            </Link>
            <Link
              className="beyond-card"
              to="/communities/sailors-haven-sunken-forest"
            >
              <div className="bc-img">
                <ImagePlaceholder
                  label="Sunken Forest boardwalk under the holly canopy"
                  rounded={false}
                  src={sunkenForestImg}
                  objectPosition="center 50%"
                />
              </div>
              <div className="bc-body">
                <span className="bc-name">Sunken Forest</span>
                <span className="bc-arrow">→</span>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* SECTION 4 — WATER TAXI */}
    <section className="explore-section explore-watertaxi">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-md-6">
            <ImagePlaceholder
              label="Water taxi underway, or pulling up to a community dock"
              ratio="4 / 3"
              src={waterTaxiImg}
            />
          </div>
          <div className="col-md-6">
            <div className="explore-heading">
              <h2>Getting around by water taxi.</h2>
            </div>
            <p>
              Feeling adventurous? Take a Fire Island Water Taxi “lateral”
              service and explore more of the island.
            </p>
            <p>
              You don't have to pick just one town. The Fire Island Water Taxi
              runs between communities all day — schedule a pick-up and go
              wherever the day takes you.
            </p>
            <p className="wt-how">
              <strong>How it works:</strong> show up at a community dock, check
              the schedule for pick-up times or call, and pay on board or via
              the ticket app.
            </p>
            <p className="wt-try">
              <strong>Try a multi-town day:</strong>
            </p>
            <ul className="wt-list">
              {WATER_TAXI_DAYS.map((day) => (
                <li key={day}>{day}</li>
              ))}
            </ul>
            <a
              className="btn btn-primary"
              href="https://www.fireislandwatertaxi.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Plan a water taxi day →
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* SECTION 5 — PLAN YOUR TRIP */}
    <section className="explore-section explore-plan" id="plan">
      <div className="container">
        <div className="explore-heading text-center">
          <h2>Plan your trip.</h2>
          <p>You've got the inspiration. Now, let's cover the logistics.</p>
        </div>
        <p className="plan-intro text-center">
          Secure tickets at the booth at each terminal (we accept cash, major
          credit cards, and Apple Pay) or through the FI mTickets app or Uber
          app. Download before you go.
        </p>
      </div>
      <AppInfo />
      {/* <div className="container">
        <div className="row plan-links">
          {PLAN_LINKS.map((l) => (
            <div className="col-md-4" key={l.title}>
              <div className="plan-link-card">
                <i className={l.icon} />
                <h3>{l.title}</h3>
                <p>{l.desc}</p>
                <Link className="btn btn-outline-primary" to={l.to}>
                  {l.cta} →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div> */}
    </section>

    {/* SECTION 6 — COMING SOON */}
    {/* <section className="explore-section explore-videos" id="videos">
      <div className="container">
        <div className="explore-heading text-center">
          <h2>See Fire Island before you go.</h2>
          <p>
            A quick visual guide to our beloved communities. Town tours, day
            trip guides, and insider tips — coming soon this season.
          </p>
        </div>
        <div className="row">
          {TOWN_ORDER.slice(0, 6).map((name) => (
            <div className="col-6 col-md-4" key={name}>
              <div className="video-tile">
                <ImagePlaceholder label={`${name} town tour`} rounded={false} />
                <div className="video-overlay">
                  <i className="far fa-play-circle" />
                  <span className="coming-soon">Coming Soon</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="video-signup text-center">
          <h3>Be the first to know when videos drop.</h3>
          <a
            href="http://eepurl.com/g_Uzdb"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Sign Up for Email Notifications
          </a>
        </div>
      </div>
    </section> */}
  </div>
);
